import { doc, getDoc, addDoc, collection } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Generates a unique campaign ID for security and tracking
 * Format: Wiin_CleanedTitle_Timestamp_Random3Digits
 * Example: Wiin_Vind_Cola_1732482000123_847
 */
function generateUniqueCampaignId(originalTitle: string): string {
  // Clean the original title: remove special chars, limit length
  const cleanTitle = originalTitle
    .replace(/[^a-zA-Z0-9æøåÆØÅ]/g, '_')
    .replace(/_+/g, '_') // Replace multiple underscores with single
    .substring(0, 30)
    .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
  
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  
  return `Wiin_${cleanTitle}_${timestamp}_${random}`;
}

/**
 * Duplicates an existing campaign with a new priority and mainCategory
 * Automatically generates a unique campaignId for security
 * @param sourceCampaignId - The Firestore document ID of the campaign to duplicate
 * @param newPriority - The priority number for the new campaign
 * @param modifications - Optional fields to modify in the duplicate
 */
export async function duplicateCampaign(
  sourceCampaignId: string,
  newPriority: number,
  modifications?: {
    title?: string;
    description?: string;
    category?: 'Mad' | 'Sport' | 'Beauty' | 'Tøj';
    mainCategory?: ('popular' | 'anbefalet')[];
    // NOTE: Prize information (prizeName, numberOfPrizes) is provided by Playable via webhook, not stored in campaign
  }
) {
  try {
    console.log(`🔄 Duplicating campaign: ${sourceCampaignId}...`);
    
    // Get the source campaign
    const sourceDoc = await getDoc(doc(db, 'campaigns', sourceCampaignId));
    
    if (!sourceDoc.exists()) {
      console.error('❌ Source campaign not found');
      return null;
    }
    
    const sourceData = sourceDoc.data();
    
    // Generate new unique campaignId
    const newCampaignId = generateUniqueCampaignId(
      modifications?.title || sourceData.title
    );
    
    console.log(`🆔 Generated new campaignId: ${newCampaignId}`);
    
    // Create a copy with modified fields
    const newCampaign = {
      ...sourceData,
      campaignId: newCampaignId, // NEW: Unique identifier for Playable/webhooks
      priority: newPriority,
      createdAt: new Date(),
      // Apply any modifications
      ...(modifications?.title && { title: modifications.title }),
      ...(modifications?.description && { description: modifications.description }),
      ...(modifications?.category && { category: modifications.category }),
      ...(modifications?.mainCategory && { mainCategory: modifications.mainCategory }),
    };
    
    // Add to campaigns collection (Firestore auto-generates document ID)
    const newDocRef = await addDoc(collection(db, 'campaigns'), newCampaign);
    console.log('✅ Campaign duplicated successfully!');
    console.log('📝 New Firestore document ID:', newDocRef.id);
    console.log('🆔 New campaignId (for Playable):', newCampaignId);
    console.log('🎯 Priority:', newPriority);
    console.log('📂 Main Category:', modifications?.mainCategory || sourceData.mainCategory);
    
    return {
      firestoreDocId: newDocRef.id,
      campaignId: newCampaignId
    };
    
  } catch (error) {
    console.error('❌ Error duplicating campaign:', error);
    return null;
  }
}

/**
 * Quick duplicate - just change priority and optionally mark as copy
 */
export async function quickDuplicate(
  sourceCampaignId: string,
  newPriority: number,
  markAsCopy: boolean = false
) {
  if (markAsCopy) {
    const sourceDoc = await getDoc(doc(db, 'campaigns', sourceCampaignId));
    if (sourceDoc.exists()) {
      const sourceData = sourceDoc.data();
      return duplicateCampaign(sourceCampaignId, newPriority, {
        title: `${sourceData.title} (Kopi)`
      });
    }
  }

  return duplicateCampaign(sourceCampaignId, newPriority);
}

// Example usage (uncomment to test):
// quickDuplicate('your-campaign-id-here', 4, true);

